import { useEffect, useRef, useState, type KeyboardEvent } from "react";

import { useQuery } from "@tanstack/react-query";
import { applyHomeEndKey } from "@recruiter/shared";

import { formatHour, formatUsd } from "../../lib/format";
import { useSaveSellerPreferences } from "../../lib/mutations/seller";
import {
  draftFromValues,
  MAX_RULE_CHARS,
  MAX_RULES,
  updateFromDraft,
  WEEKDAYS,
  WORKDAYS,
  type DraftField,
  type PreferencesDraft,
} from "../../lib/preferences-form";
import { preferencesQueryOptions, SellerQueryError } from "../../lib/queries/seller";
import { useSellerStore } from "../../lib/store/seller-store";
import { CalendarIcon, ClockIcon, CloseIcon, PlusIcon, SlidersIcon, WalletIcon } from "./icons";

const HOURS = Array.from({ length: 25 }, (_, h) => h);

// The talent's standing preferences, edited as ONE form: minimum rates, when they work,
// and work they do not take. Saving REPLACES the set and applies at once (product decision
// 2026-09-30): the server writes it live, and search and outreach use it from the next
// project on. The draft is the form's own edit buffer (UI state); the saved values stay in
// the Query cache, and a successful save writes the server's answer back there.
export function PreferencesDrawer() {
  const sellerId = useSellerStore((s) => s.activeSellerId);
  const setPrefsOpen = useSellerStore((s) => s.setPrefsOpen);
  const q = useQuery(preferencesQueryOptions(sellerId));
  const save = useSaveSellerPreferences();
  const [edited, setEdited] = useState<PreferencesDraft | null>(null);
  const [errors, setErrors] = useState<Partial<Record<DraftField, string>>>({});
  const [ruleInput, setRuleInput] = useState("");
  const panelRef = useRef<HTMLDivElement | null>(null);

  const draft = edited ?? (q.data ? draftFromValues(q.data.preferences) : null);
  const close = () => setPrefsOpen(false);

  // Focus lands in the panel when it opens; Escape closes it.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key === "Escape") {
      e.stopPropagation();
      close();
    }
  }

  function patch(next: Partial<PreferencesDraft>): void {
    if (!draft) return;
    setEdited({ ...draft, ...next });
    save.reset();
  }

  function toggleDay(day: number): void {
    if (!draft) return;
    const days = draft.days.includes(day)
      ? draft.days.filter((d) => d !== day)
      : [...draft.days, day].sort((a, b) => a - b);
    patch({ days });
  }

  function addRule(): void {
    if (!draft) return;
    const rule = ruleInput.replace(/\s+/g, " ").trim();
    if (!rule) return;
    if (!draft.rules.some((r) => r.toLowerCase() === rule.toLowerCase())) {
      patch({ rules: [...draft.rules, rule] });
    }
    setRuleInput("");
  }

  async function submit(): Promise<void> {
    if (!draft) return;
    const out = updateFromDraft(draft);
    if (!out.ok) {
      setErrors(out.errors);
      return;
    }
    setErrors({});
    try {
      await save.mutateAsync({ sellerId, body: out.body });
      close();
    } catch {
      // Surfaced from `save.error` below, never swallowed.
    }
  }

  const categoryMinimums = q.data?.preferences.category_minimums ?? [];
  const saveError =
    save.error instanceof SellerQueryError
      ? save.error.message
      : save.error
        ? "Couldn't save your preferences. Try again."
        : null;

  return (
    <div className="sl-drawer" role="presentation">
      <button
        type="button"
        className="sl-drawer__scrim"
        aria-label="Close your preferences"
        tabIndex={-1}
        onClick={close}
      />
      <div
        ref={panelRef}
        className="sl-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sl-prefs-title"
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <header className="sl-drawer__head">
          <span className="sl-drawer__badge" aria-hidden="true">
            <SlidersIcon size={18} />
          </span>
          <div className="sl-drawer__heading">
            <h2 id="sl-prefs-title" className="sl-drawer__title">
              Your work preferences
            </h2>
            <p className="sl-drawer__lede">
              Mira only brings you projects that fit. Changes apply to new projects right away.
            </p>
          </div>
          <button type="button" className="sl-icon-btn" onClick={close} aria-label="Close">
            <CloseIcon size={18} />
          </button>
        </header>

        {q.isPending || !draft ? (
          <div className="sl-drawer__body">
            <p className="sl-hint">{q.isError ? "Couldn't load your preferences." : "Loading…"}</p>
          </div>
        ) : (
          <div className="sl-drawer__body">
            {q.data?.status === "in_review" ? (
              <p className="sl-drawer__review" role="note">
                Something you told Mira in a conversation is waiting for review. It is filled in
                below; saving here applies it now.
              </p>
            ) : null}

            <fieldset className="sl-fieldset">
              <legend className="sl-fieldset__legend">
                <WalletIcon size={16} /> Minimum rates
              </legend>
              <p className="sl-fieldset__help">
                Projects that pay less than these skip you. Leave one empty to take any amount.
              </p>
              <MoneyField
                label="Project budget"
                unit="per project"
                value={draft.minProject}
                error={errors.minProject}
                onChange={(minProject) => patch({ minProject })}
              />
              <MoneyField
                label="Hourly rate"
                unit="per hour"
                value={draft.minHourly}
                error={errors.minHourly}
                onChange={(minHourly) => patch({ minHourly })}
              />
              <MoneyField
                label="Monthly rate"
                unit="per month"
                value={draft.minMonthly}
                error={errors.minMonthly}
                onChange={(minMonthly) => patch({ minMonthly })}
              />
            </fieldset>

            <fieldset className="sl-fieldset">
              <legend className="sl-fieldset__legend">
                <ClockIcon size={16} /> When you work
              </legend>
              <p className="sl-fieldset__help">
                Mira's first message waits for your working days and hours, in your own time zone.
                You still show up in every search.
              </p>
              <div className="sl-days" role="group" aria-label="Working days">
                {WEEKDAYS.map((w) => {
                  const on = draft.days.includes(w.day);
                  return (
                    <button
                      key={w.day}
                      type="button"
                      className="sl-day"
                      aria-pressed={on}
                      aria-label={w.short}
                      title={w.short}
                      onClick={() => toggleDay(w.day)}
                    >
                      {w.letter}
                    </button>
                  );
                })}
                <span className="sl-days__presets">
                  <button
                    type="button"
                    className="sl-chip-btn"
                    onClick={() => patch({ days: [...WORKDAYS] })}
                  >
                    Weekdays
                  </button>
                  <button
                    type="button"
                    className="sl-chip-btn"
                    onClick={() => patch({ days: WEEKDAYS.map((w) => w.day) })}
                  >
                    Every day
                  </button>
                </span>
              </div>
              {errors.days ? <p className="sl-field-error">{errors.days}</p> : null}

              <label className="sl-switch">
                <input
                  type="checkbox"
                  checked={draft.hoursOn}
                  onChange={(e) => patch({ hoursOn: e.target.checked })}
                />
                <span className="sl-switch__track" aria-hidden="true">
                  <span className="sl-switch__thumb" />
                </span>
                <span>Set my working hours</span>
              </label>
              {draft.hoursOn ? (
                <div className="sl-hours">
                  <label className="sl-select">
                    <span className="sl-select__label">From</span>
                    <select
                      value={draft.hoursStart}
                      onChange={(e) => patch({ hoursStart: Number(e.target.value) })}
                    >
                      {HOURS.slice(0, 24).map((h) => (
                        <option key={h} value={h}>
                          {formatHour(h)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="sl-select">
                    <span className="sl-select__label">To</span>
                    <select
                      value={draft.hoursEnd}
                      onChange={(e) => patch({ hoursEnd: Number(e.target.value) })}
                    >
                      {HOURS.slice(1).map((h) => (
                        <option key={h} value={h}>
                          {formatHour(h)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <DayBar start={draft.hoursStart} end={draft.hoursEnd} />
                </div>
              ) : (
                <p className="sl-hint">
                  No set hours: Mira writes to you between 07:00 and midnight.
                </p>
              )}
              {errors.hours ? <p className="sl-field-error">{errors.hours}</p> : null}

              <div className="sl-date">
                <label className="sl-select">
                  <span className="sl-select__label">
                    <CalendarIcon size={14} /> Free to start from
                  </span>
                  <input
                    type="date"
                    value={draft.availableFrom}
                    onChange={(e) => patch({ availableFrom: e.target.value })}
                  />
                </label>
                {draft.availableFrom ? (
                  <button
                    type="button"
                    className="sl-link"
                    onClick={() => patch({ availableFrom: "" })}
                  >
                    Clear
                  </button>
                ) : (
                  <span className="sl-hint">Empty means you can start any time.</span>
                )}
              </div>
            </fieldset>

            <fieldset className="sl-fieldset">
              <legend className="sl-fieldset__legend">Work you don't take</legend>
              <p className="sl-fieldset__help">
                About the kind of work only, for example "No crypto projects". Mira can't filter
                clients by who they are.
              </p>
              {draft.rules.length > 0 ? (
                <ul className="sl-rules">
                  {draft.rules.map((rule) => (
                    <li key={rule} className="sl-rule">
                      <span>{rule}</span>
                      <button
                        type="button"
                        className="sl-rule__x"
                        aria-label={`Remove "${rule}"`}
                        onClick={() => patch({ rules: draft.rules.filter((r) => r !== rule) })}
                      >
                        <CloseIcon size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {draft.rules.length < MAX_RULES ? (
                <div className="sl-rule-add">
                  <input
                    className="sl-input"
                    value={ruleInput}
                    maxLength={MAX_RULE_CHARS}
                    placeholder="Add a kind of work to skip"
                    aria-label="Add a kind of work you don't take"
                    onChange={(e) => setRuleInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addRule();
                        return;
                      }
                      applyHomeEndKey(e);
                    }}
                  />
                  <button
                    type="button"
                    className="sl-icon-btn sl-icon-btn--soft"
                    onClick={addRule}
                    disabled={!ruleInput.trim()}
                    aria-label="Add"
                  >
                    <PlusIcon size={16} />
                  </button>
                </div>
              ) : null}
              {errors.rules ? <p className="sl-field-error">{errors.rules}</p> : null}
            </fieldset>

            {categoryMinimums.length > 0 ? (
              <fieldset className="sl-fieldset">
                <legend className="sl-fieldset__legend">Set with Mira</legend>
                <p className="sl-fieldset__help">
                  Minimums for specific kinds of work, from your conversations. They stay as they
                  are.
                </p>
                <ul className="sl-rules">
                  {categoryMinimums.map((c) => (
                    <li key={c.label} className="sl-rule sl-rule--static">
                      <span>
                        {c.label}: {formatUsd(c.usd)}+
                      </span>
                    </li>
                  ))}
                </ul>
              </fieldset>
            ) : null}
          </div>
        )}

        <footer className="sl-drawer__foot">
          <span className="sl-drawer__status" role="status">
            {saveError ? <span className="sl-field-error">{saveError}</span> : null}
          </span>
          <button type="button" className="sl-btn sl-btn--ghost" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="sl-btn"
            onClick={() => void submit()}
            disabled={!draft || save.isPending}
          >
            {save.isPending ? "Saving…" : "Save preferences"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function MoneyField({
  label,
  unit,
  value,
  error,
  onChange,
}: {
  label: string;
  unit: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="sl-money-field">
      <span className="sl-money-field__label">{label}</span>
      <span className="sl-money" data-invalid={!!error}>
        <span className="sl-money__sign" aria-hidden="true">
          $
        </span>
        <input
          inputMode="numeric"
          value={value}
          placeholder="Any"
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={applyHomeEndKey}
          aria-invalid={!!error}
        />
        <span className="sl-money__unit">{unit}</span>
      </span>
      {error ? <span className="sl-field-error">{error}</span> : null}
    </label>
  );
}

// A 24-hour strip with the working window lit: the shape of the day at a glance.
function DayBar({ start, end }: { start: number; end: number }) {
  return (
    <div className="sl-daybar" aria-hidden="true">
      {HOURS.slice(0, 24).map((h) => (
        <span key={h} className="sl-daybar__h" data-on={h >= start && h < end} />
      ))}
      <span className="sl-daybar__ticks">
        <span>00</span>
        <span>06</span>
        <span>12</span>
        <span>18</span>
        <span>24</span>
      </span>
    </div>
  );
}
